import { VIEWER_COMPONENTS_ID_PREFIXES } from './componentUtils'

export interface DomSelectorOptions {
	experiments?: any
	document?: Document
	logger?: any
	componentIds?: Set<string>
}

export function getShouldUseClassSelector(options: DomSelectorOptions = {}): boolean {
	let experiments = options?.experiments

	// Fallback to window.viewerModel.experiments if not provided
	if (!experiments && typeof window !== 'undefined') {
		try {
			const windowAny = window as any
			experiments = windowAny.viewerModel?.experiments
		} catch (error) {
			// Silent fail - experiments not available yet
		}
	}

	if (!experiments) {
		return false
	}

	const useClassSelectors = experiments['specs.thunderbolt.useClassSelectorsForLookup']
	const addIdAsClassName = experiments['specs.thunderbolt.addIdAsClassName']
	return !!(useClassSelectors && addIdAsClassName)
}

export function getDocument(options: DomSelectorOptions = {}): Document | null {
	return options.document || (typeof document !== 'undefined' ? document : null)
}

function logFallbackToId(logger: any, compId: string, selectorType: string): void {
	// Track fallback in BI to identify components missing className
	if (logger && typeof logger.meter === 'function') {
		logger.meter('dom_selector_id_fallback', {
			customParams: {
				compId,
				selectorType,
			},
		})
	}

	// Always log to console for debugging
	if (typeof console !== 'undefined' && console.warn) {
		console.warn(`[DOM Selectors] Fallback to ID for '${compId}' (${selectorType}).`)
	}
}

/**
 * Get element by component ID with optional class selector fallback.
 *
 * When experiments are enabled:
 * 1. Try querySelector with class selector (.compId)
 * 2. Fallback to getElementById if not found
 * 3. Log fallback for tracking
 *
 * When experiments are disabled:
 * - Use getElementById directly
 */
export function getByCompId(compId: string, options: DomSelectorOptions = {}): HTMLElement | null {
	const doc = getDocument(options)
	if (!doc) return null

	if (!compId || typeof compId !== 'string') {
		return null
	}

	const shouldUseClassSelector = getShouldUseClassSelector(options)

	if (shouldUseClassSelector) {
		const element = doc.querySelector(`.${compId}`) as HTMLElement | null
		if (element) {
			return element
		}
	}

	const element = doc.getElementById(compId)
	if (element && shouldUseClassSelector) {
		logFallbackToId(options?.logger, compId, 'getElementById')
	}
	return element
}

/**
 * Get component ID from an element.
 *
 * When experiments are enabled:
 * 1. Look for component ID in classList (class-based selector)
 * 2. Fallback to element.id if not found
 * 3. Log fallback for tracking
 *
 * When experiments are disabled:
 * - Return element.id directly
 */
export function getElementCompId(element: Element, options: DomSelectorOptions = {}): string {
	const shouldUseClassSelector = getShouldUseClassSelector(options)

	if (shouldUseClassSelector) {
		const classes = Array.from(element.classList || [])

		// If specific componentIds set is provided, check against it
		if (options.componentIds) {
			const compId = classes.find((cls) => options.componentIds!.has(cls))
			if (compId) {
				return compId
			}
		}

		// Fallback: Look for component ID pattern (comp-*, TPASection_*, etc.)
		const compId = classes.find((cls) => VIEWER_COMPONENTS_ID_PREFIXES.some((prefix) => cls.startsWith(prefix)))
		if (compId) {
			return compId
		}
	}

	// Fallback to element.id
	const elementId = element.id
	if (elementId && shouldUseClassSelector) {
		logFallbackToId(options.logger, elementId, 'getElementCompId')
	}

	return elementId
}

/**
 * Converts an ID-based selector to a class-based selector.
 * Handles various selector patterns:
 * - #id → .id
 * - [id="value"] → [class~="value"]
 * - [id^="value"] → [class^="value"]
 * - [id*="value"] → [class*="value"]
 * - [id$="value"] → [class$="value"]
 *
 * @param selector - The ID-based selector to convert
 * @returns The class-based selector
 */
export function convertIdSelectorToClassSelector(selector: string): string {
	return (
		selector
			// Replace #id with .id
			.replace(/#([a-zA-Z0-9_-]+)/g, '.$1')
			// Replace [id="value"] with [class~="value"] (exact match in space-separated list)
			.replace(/\[id="([^"]+)"\]/g, '[class~="$1"]')
			// Replace [id^="value"] with [class^="value"] (starts with)
			.replace(/\[id\^="([^"]+)"\]/g, '[class^="$1"]')
			// Replace [id*="value"] with [class*="value"] (contains)
			.replace(/\[id\*="([^"]+)"\]/g, '[class*="$1"]')
			// Replace [id$="value"] with [class$="value"] (ends with)
			.replace(/\[id\$="([^"]+)"\]/g, '[class$="$1"]')
	)
}

/**
 * Generic querySelector that automatically converts ID-based selectors to class-based selectors
 * when the experiment is enabled. Falls back to ID-based selectors if no elements are found.
 *
 * @param selector - The selector string (can include #id, [id^="..."], etc.)
 * @param options - DOM selector options
 * @returns The first matching element or null
 */
export function querySelector(selector: string, options: DomSelectorOptions = {}): Element | null {
	const doc = getDocument(options)
	if (!doc) return null

	if (!selector || typeof selector !== 'string') return null

	const shouldUseClassSelector = getShouldUseClassSelector(options)

	if (shouldUseClassSelector) {
		const classSelector = convertIdSelectorToClassSelector(selector)
		const element = doc.querySelector(classSelector)
		if (element) {
			return element
		}
	}

	const element = doc.querySelector(selector)
	if (element && shouldUseClassSelector) {
		logFallbackToId(options.logger, selector, 'querySelector')
	}

	return element
}

/**
 * Generic querySelectorAll that automatically converts ID-based selectors to class-based selectors
 * when the experiment is enabled. Falls back to ID-based selectors if no elements are found.
 *
 * @param selector - The selector string (can include #id, [id^="..."], etc.)
 * @param options - DOM selector options
 * @returns Array of matching elements
 */
export function querySelectorAll(selector: string, options: DomSelectorOptions = {}): Element[] {
	const doc = getDocument(options)

	if (!doc) return []
	if (!selector || typeof selector !== 'string') return []

	const shouldUseClassSelector = getShouldUseClassSelector(options)

	if (shouldUseClassSelector) {
		const classSelector = convertIdSelectorToClassSelector(selector)
		const elements = Array.from(doc.querySelectorAll(classSelector))
		if (elements.length > 0) {
			return elements
		}
	}

	const elements = Array.from(doc.querySelectorAll(selector))
	if (elements.length > 0 && shouldUseClassSelector) {
		logFallbackToId(options.logger, selector, 'querySelectorAll')
	}

	return elements
}

/**
 * Find closest ancestor element by component ID.
 * Supports both class-based and ID-based selectors.
 *
 * @param element - The starting element
 * @param compId - The component ID to search for
 * @param options - DOM selector options
 * @returns The closest matching ancestor element or null
 */
export function getClosestByCompId(element: Element, compId: string, options: DomSelectorOptions = {}): Element | null {
	if (!compId || typeof compId !== 'string') {
		return null
	}

	const shouldUseClassSelector = getShouldUseClassSelector(options)

	if (shouldUseClassSelector) {
		const closestElement = element.closest(`.${compId}`)
		if (closestElement) {
			return closestElement
		}
	}

	const closestElement = element.closest(`#${compId}`)
	if (closestElement && shouldUseClassSelector) {
		logFallbackToId(options.logger, compId, 'getClosestByCompId')
	}

	return closestElement
}
